import { useTranslation } from 'react-i18next';
import { Mail, Phone, MapPin, Globe, FileText } from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { cn } from '@/lib/utils';

// Any non-dedicated cari type code (built-in generics plus custom types).
type BasicCariType = string;

const typeBadgeStyles: Record<string, string> = {
  ORT: 'bg-amber-100 text-amber-700',
  P: 'bg-teal-100 text-teal-700',
  AH: 'bg-slate-100 text-slate-700',
  E: 'bg-red-100 text-red-700',
  X: 'bg-pink-100 text-pink-700',
  I: 'bg-cyan-100 text-cyan-700',
};

const typeLabelKey: Record<string, string> = {
  ORT: 'cari.partner',
  P: 'cari.personnel',
  AH: 'cari.openAccount',
  E: 'cari.government',
  X: 'cari.legal',
  I: 'cari.insurance',
};

interface BasicCariDetailContentProps {
  cariType: BasicCariType;
  supplier: any;
  /** Display label for custom types (registry name). */
  typeLabel?: string;
}

export function BasicCariDetailContent({ cariType, supplier, typeLabel }: BasicCariDetailContentProps) {
  const { t } = useTranslation('trading');
  const { t: tCommon } = useTranslation('common');

  const resolvedTypeLabel =
    typeLabel ?? (typeLabelKey[cariType] ? t(typeLabelKey[cariType]) : cariType);
  const badgeStyle = typeBadgeStyles[cariType] ?? 'bg-zinc-100 text-zinc-700';

  const paymentTermsLabels: Record<string, string> = {
    PREPAID: 'Prepaid',
    CREDIT_15: 'Net 15',
    CREDIT_30: 'Net 30',
    CREDIT_60: 'Net 60',
    CREDIT_90: 'Net 90',
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">{tCommon('status')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{tCommon('status')}</span>
            <span
              className={cn(
                'px-2 py-0.5 text-xs font-medium',
                supplier.isActive
                  ? 'bg-green-100 text-green-700'
                  : 'bg-gray-100 text-gray-700'
              )}
            >
              {supplier.isActive ? tCommon('active') : tCommon('inactive')}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">{resolvedTypeLabel}</span>
            <span className={`px-2 py-0.5 text-xs font-medium ${badgeStyle}`}>
              {cariType} - {resolvedTypeLabel}
            </span>
          </div>
          {supplier.paymentTerms && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                {t('purchasing.suppliers.paymentTerms')}
              </span>
              <span className="font-medium">
                {paymentTermsLabels[supplier.paymentTerms] || supplier.paymentTerms}
              </span>
            </div>
          )}
          {supplier.taxId && (
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">
                {t('purchasing.suppliers.taxId')}
              </span>
              <span className="font-medium">{supplier.taxId}</span>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('customers.primaryContact')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {supplier.contactName && supplier.contactName !== 'N/A' && (
            <div className="font-medium">{supplier.contactName}</div>
          )}
          {supplier.contactTitle && (
            <div className="text-sm text-muted-foreground">{supplier.contactTitle}</div>
          )}
          {supplier.email && (
            <div className="flex items-center gap-2 text-sm">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <a
                href={`mailto:${supplier.email}`}
                className="text-primary hover:underline"
              >
                {supplier.email}
              </a>
            </div>
          )}
          {supplier.phone && (
            <div className="flex items-center gap-2 text-sm">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <a href={`tel:${supplier.phone}`} className="hover:underline">
                {supplier.phone}
              </a>
            </div>
          )}
          {supplier.website && (
            <div className="flex items-center gap-2 text-sm">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <a
                href={supplier.website}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                {supplier.website}
              </a>
            </div>
          )}
          {!supplier.email && !supplier.phone && !supplier.website && (
            <p className="text-sm text-muted-foreground">-</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('customers.primaryAddress')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {supplier.addressLine1 || supplier.city || supplier.country ? (
            <div className="flex items-start gap-2 text-sm">
              <MapPin className="h-4 w-4 text-muted-foreground mt-0.5" />
              <div>
                {supplier.addressLine1 && <div>{supplier.addressLine1}</div>}
                {supplier.addressLine2 && <div>{supplier.addressLine2}</div>}
                <div>
                  {[supplier.city, supplier.state, supplier.postalCode]
                    .filter(Boolean)
                    .join(', ')}
                </div>
                {supplier.country && <div>{supplier.country}</div>}
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">-</p>
          )}
        </CardContent>
      </Card>

      {supplier.notes && (
        <Card className="md:col-span-2 lg:col-span-3">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="h-4 w-4" />
              {t('purchasing.suppliers.notes')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">{supplier.notes}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
